/**
 * Krok 2b, Očištění zisku pro investora (SDE / Adjusted EBITDA)
 * Volitelný krok mezi Finance (Step1) a KPIs (Step2).
 * Pokud uživatel krok vyplní, všechny další výpočty používají Adjusted EBITDA.
 * Pokud přeskočí, pokračuje se s původní EBITDA.
 */
import React, { useState, useEffect } from 'react';
import { TooltipLabel } from './TooltipLabel';
import { posunNaChybu } from '../utils/chyby';

const DEFAULT_MARKET_RATE = 60000; // Kč/měsíc pro 40h týdně

function n(v) { return Number(v) || 0; }

function fmt(v) {
  if (v === null || v === undefined || isNaN(v)) return '-';
  return Math.round(v).toLocaleString('cs-CZ');
}

function PreviewRow({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.45rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{label}</span>
      <span style={{
        fontSize: '0.95rem', fontWeight: highlight ? 700 : 500,
        color: highlight ? 'var(--purple-light)' : 'var(--star-white)',
      }}>
        {fmt(value)} Kč
      </span>
    </div>
  );
}

function DeltaRow({ label, value }) {
  if (!value) return null;
  const positive = value >= 0;
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '0.45rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{label}</span>
      <span style={{
        fontSize: '0.9rem', fontWeight: 600,
        color: positive ? 'var(--color-success)' : 'var(--color-danger)',
      }}>
        {positive ? '+' : ''}{fmt(value)} Kč
      </span>
    </div>
  );
}

export function StepSDE_ProfitCleaning({ wizardState, setWizardState, onNext, onBack }) {
  const finance = wizardState.finance;
  const saved = wizardState.profitCleaning || {};

  const rawRevenue = n(finance.revenue);
  const rawCogs    = n(finance.cogs);
  const rawOpex    = n(finance.opex);
  const rawEbitda  = rawRevenue - rawCogs - rawOpex;

  const [hours, setHours] = useState(saved.hours_per_week ?? 40);
  const [ownerPay, setOwnerPay] = useState(saved.owner_pay ?? '');
  const [marketReplacement, setMarketReplacement] = useState(saved.market_replacement ?? '');
  const [marketTouched, setMarketTouched] = useState(saved.market_replacement_touched ?? false);

  const [personalCosts, setPersonalCosts]   = useState(saved.personal_costs ?? '');
  const [oneTimeCosts, setOneTimeCosts]     = useState(saved.one_time_costs ?? '');
  const [familyWages, setFamilyWages]       = useState(saved.family_wages ?? '');
  const [marketingTest, setMarketingTest]   = useState(saved.marketing_test ?? '');
  const [oneTimeIncome, setOneTimeIncome]   = useState(saved.one_time_income ?? '');

  // Jedna otázka rozhodne, kterou metriku použijeme: 'owner' (SDE) | 'manager' (Adjusted EBITDA)
  const [ownerChoice, setOwnerChoice] = useState(saved.owner_choice ?? '');
  const [choiceError, setChoiceError] = useState(false);

  // Auto-compute default market replacement from hours (unless user touched it)
  useEffect(() => {
    if (!marketTouched) {
      const defaultRate = Math.round((hours / 40) * DEFAULT_MARKET_RATE);
      setMarketReplacement(defaultRate > 0 ? String(defaultRate) : '');
    }
  }, [hours, marketTouched]);

  // ── Výpočty ────────────────────────────────────────────────────────────────
  // Měsíční hodnoty → roční (EBITDA a add-backs jsou roční)
  const ownerPayMonthly          = n(ownerPay);
  const ownerPayAnnual           = ownerPayMonthly * 12;
  const marketReplacementMonthly = n(marketReplacement) || Math.round((hours / 40) * DEFAULT_MARKET_RATE);
  const marketReplacementAnnual  = marketReplacementMonthly * 12;
  // Mzda v nákladech nese i odvody zaměstnavatele (sociální a zdravotní
  // 33,8 %), proto se rozdíl proti tržní mzdě počítá s nimi. Dřív se tržní
  // náhrada porovnávala bez odvodů a upravený zisk vycházel vyšší.
  const ODVODY = 1.338;
  const wageCorrection           = (ownerPayAnnual - marketReplacementAnnual) * ODVODY; // roční rozdíl

  // Jednorázové výnosy se nezopakují, proto se od zisku odečítají.
  const totalAddbacks = n(personalCosts) + n(oneTimeCosts) + n(familyWages) + n(marketingTest) - n(oneTimeIncome);

  // SDE = EBITDA + roční odměna majitele + add-backs
  // (jako by firma fungovala bez majitele, vše osobní odfiltrováno)
  const sde = rawEbitda + ownerPayAnnual + totalAddbacks;

  // Adjusted EBITDA = EBITDA normalizovaná na tržní náhradu manažera (roční)
  const adjustedEbitda = rawEbitda + wageCorrection + totalAddbacks;

  // ── Uložit a pokračovat ────────────────────────────────────────────────────
  const handleComplete = () => {
    if (!ownerChoice) { setChoiceError(true); posunNaChybu(); return; }
    // Hodnota se vždy počítá z upravené EBITDA po tržní mzdě vedení. Kupující
    // za práci majitele neplatí, musí ji nahradit. Dřív se u volby „zůstanu
    // řídit“ násobil zisk pro majitele (SDE) násobkem pro EBITDA a hodnota
    // vyšla o desítky procent vyšší (plán v2, M6). SDE zůstává pro informaci.
    const chosenEbitda = adjustedEbitda;
    const state = {
      hours_per_week: hours,
      owner_pay: ownerPay,
      market_replacement: marketReplacement,
      market_replacement_touched: marketTouched,
      personal_costs: personalCosts,
      one_time_costs: oneTimeCosts,
      family_wages: familyWages,
      marketing_test: marketingTest,
      one_time_income: oneTimeIncome,
      owner_choice: ownerChoice,
      // Vypočtené hodnoty
      sde: Math.round(sde),
      adjusted_ebitda: Math.round(adjustedEbitda),
      raw_ebitda: Math.round(rawEbitda),
      wage_correction: Math.round(wageCorrection),
      total_addbacks: Math.round(totalAddbacks),
      skipped: false,
    };
    setWizardState(prev => ({
      ...prev,
      profitCleaning: state,
      // Použijeme zvolenou metriku jako ebitda_override pro backend
      finance: { ...prev.finance, ebitda_override: Math.round(chosenEbitda) },
    }));
    onNext();
  };

  // Přeskočení nepřepočítává odměnu majitele (záleží na tom, kdo firmu
  // povede), ale jednorázové a osobní položky, které klient zadal nebo
  // potvrdil v rozboru, nezahodí.
  const handleSkip = () => {
    setWizardState(prev => ({
      ...prev,
      profitCleaning: {
        ...(prev.profitCleaning || {}),
        personal_costs: personalCosts, one_time_costs: oneTimeCosts,
        family_wages: familyWages, marketing_test: marketingTest, one_time_income: oneTimeIncome,
        skipped: true, owner_choice: '', sde: null, adjusted_ebitda: null,
        raw_ebitda: Math.round(rawEbitda), total_addbacks: Math.round(totalAddbacks),
      },
      finance: { ...prev.finance, ebitda_override: totalAddbacks ? Math.round(rawEbitda + totalAddbacks) : null },
    }));
    onNext();
  };

  const wageWarning = ownerPayMonthly > 0 && wageCorrection < 0;
  // Preview se zobrazí vždy, marketReplacement má vždy default hodnotu ze slideru
  const hasAnyInput = true;

  return (
    <div className="step-container">
      {/* ── Nadpis ─────────────────────────────────────────────────────────── */}
      <h2 style={{ marginBottom: '0.4rem' }}>Očištění zisku (volitelné)</h2>
      <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
        V účetnictví bývá zisk snížený o náklady, které s běžným provozem firmy přímo nesouvisí,
        třeba osobní výdaje majitele nebo jednorázové investice. Tady je můžete odfiltrovat, ať
        výpočet vychází z vašeho skutečného provozního zisku.
      </p>

      {/* ── Skip banner ────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(107,63,160,0.18), rgba(75,142,245,0.12))',
        border: '1px solid rgba(155,89,212,0.35)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div>
          <div style={{ fontWeight: 600, color: 'var(--purple-light)', marginBottom: '0.2rem', fontSize: '0.92rem' }}>
            Doporučujeme vyplnit
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)' }}>
            Pomůže nám přesněji určit váš skutečný provozní zisk. Nemáte-li co očišťovat, klidně přeskočte.
          </div>
          {totalAddbacks !== 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--star-white)', marginTop: '0.35rem' }}>
              Při přeskočení započítáme položky z části B ({totalAddbacks > 0 ? '+' : ''}{fmt(totalAddbacks)} Kč),
              odměnu majitele nepřepočítáme.
            </div>
          )}
        </div>
        <button
          onClick={handleSkip}
          style={{
            background: 'transparent',
            border: '1px solid rgba(155,89,212,0.4)',
            color: 'var(--text-dim)',
            padding: '0.4rem 1rem',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            fontSize: '0.82rem',
            whiteSpace: 'nowrap',
          }}
        >
          Přeskočit tento krok
        </button>
      </div>

      {/* Aktuální EBITDA reference */}
      <div style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 'var(--radius)',
        padding: '0.75rem 1rem',
        marginBottom: '1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Vaše EBITDA z účetnictví</span>
        <span style={{ fontWeight: 700, color: rawEbitda >= 0 ? 'var(--color-success)' : 'var(--color-danger)', fontSize: '1.05rem' }}>
          {fmt(rawEbitda)} Kč
        </span>
      </div>

      {/* ── Jedna otázka rozhodne metriku ────────────────────────────────── */}
      <div className="edit-form" style={{ marginBottom: '1.5rem' }}>
        <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.6rem' }}>Kdo povede firmu po prodeji?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          {[
            { v: 'owner', t: 'Já zůstanu řídit firmu', d: 'Ukážeme i zisk pro majitele (SDE). Hodnotu počítáme ze zisku po tržní mzdě vedení, kupující vaši práci musí nahradit.' },
            { v: 'manager', t: 'Nahradí mě manažer', d: 'Hodnotu počítáme ze zisku po tržní mzdě manažera (Adjusted EBITDA).' },
          ].map(o => (
            <button key={o.v} type="button"
              onClick={() => { setOwnerChoice(o.v); setChoiceError(false); }}
              style={{
                textAlign: 'left', padding: '0.85rem 1rem', borderRadius: 10, cursor: 'pointer',
                border: `2px solid ${ownerChoice === o.v ? 'var(--purple-bright)' : 'rgba(155,89,212,0.2)'}`,
                background: ownerChoice === o.v ? 'rgba(107,63,160,0.12)' : 'var(--purple-dark)',
              }}>
              <div style={{ fontWeight: 600, color: 'var(--star-white)', marginBottom: '0.2rem' }}>{ownerChoice === o.v ? '● ' : '○ '}{o.t}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>{o.d}</div>
            </button>
          ))}
        </div>
        {choiceError && <small className="chyba-pole" style={{ display: 'block', marginTop: '0.4rem' }}>Vyberte prosím jednu možnost.</small>}
      </div>

      {/* ── Sekce A, Normalizace mzdy majitele ──────────────────────────── */}
      <div className="edit-form" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: 'var(--purple-light)' }}>
          A, Normalizace mzdy majitele
        </h3>

        {/* Slider hodin */}
        <div className="form-group" style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Kolik hodin týdně ve firmě reálně odpracujete?</span>
            <strong style={{ color: 'var(--purple-light)', minWidth: 50, textAlign: 'right' }}>{hours} h/týden</strong>
          </label>
          <input
            type="range" min={0} max={60} step={1} value={hours}
            onChange={e => setHours(Number(e.target.value))}
            style={{ width: '100%', marginTop: '0.5rem', accentColor: 'var(--purple-bright)', cursor: 'pointer' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '0.25rem' }}>
            <span>0 h (pasivní)</span>
            <span>40 h (full-time)</span>
            <span>60 h</span>
          </div>
        </div>

        {/* Aktuální odměna majitele */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <TooltipLabel
            htmlFor="owner-pay"
            label="Kolik si nyní z firmy měsíčně vyplácíte? (mzda + faktury)"
            tooltip="Součet všeho co dostáváte od firmy: hrubá mzda, dividendy, faktury přes IČO, auta, telefony… Zadejte měsíční částku v Kč."
          />
          <input
            id="owner-pay"
            type="number" min={0} placeholder="např. 80 000"
            value={ownerPay}
            onChange={e => setOwnerPay(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>
            Kč / měsíc{ownerPayMonthly > 0 ? ` (×12 = ${fmt(ownerPayAnnual)} Kč/rok)` : ''}
          </small>
        </div>

        {/* Tržní náhrada */}
        <div className="form-group" style={{ marginBottom: '0.5rem' }}>
          <TooltipLabel
            htmlFor="market-replacement"
            label="Kolik by stál manažer, který by dělal vaši práci?"
            tooltip="Buďte upřímní. Když firmu jen řídíte 2 hodiny týdně, náhrada je skoro nulová. Když v ní pracujete na plný úvazek, náhrada musí pokrýt někoho, kdo vás nahradí."
          />
          <input
            id="market-replacement"
            type="number" min={0}
            placeholder={`Výchozí: ${fmt(Math.round((hours / 40) * DEFAULT_MARKET_RATE))}`}
            value={marketReplacement}
            onChange={e => {
              setMarketReplacement(e.target.value);
              setMarketTouched(true);
            }}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>
            Kč / měsíc · výchozí: {fmt(Math.round((hours / 40) * DEFAULT_MARKET_RATE))} Kč ({hours}h/40h × 60 000 Kč)
          </small>
        </div>

        {/* Varování: majitel bere více než tržní */}
        {wageWarning && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 'var(--radius)', padding: '0.65rem 0.9rem',
            marginTop: '0.75rem', fontSize: '0.83rem', color: 'rgba(192,69,63,0.45)', lineHeight: 1.55,
          }}>
            <strong>Berete si více než tržní náhrada.</strong> Investor tuto mezeru odečte -
            počítá s tím, že manažera zaplatí levněji. Dopad na Adjusted EBITDA:{' '}
            <strong>{fmt(ownerPayMonthly - marketReplacementMonthly)} Kč/měsíc × 12 = {fmt(wageCorrection)} Kč/rok</strong>.
          </div>
        )}
      </div>

      {/* ── Sekce B, Add-backs ──────────────────────────────────────────── */}
      <div className="edit-form" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', color: 'var(--purple-light)' }}>
          B, Add-backs (položky k očištění zisku)
        </h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: 'var(--text-dim)' }}>
          Zadejte roční částky nákladů, které s běžným provozem přímo nesouvisí.
        </p>

        {/* Osobní náklady */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <TooltipLabel
            htmlFor="personal-costs"
            label="Osobní náklady zahrnuté ve firmě"
            tooltip="Příklad: Operativní leasing na auto které používáte i soukromě (15 000 Kč/měsíc = 180 000 Kč/rok). Nebo rodinné tarify telefonů, soukromé cestování. Investor tyto náklady přičítá zpět k zisku, pro něj nejsou provozní nezbytností."
          />
          <input
            id="personal-costs"
            type="number" min={0} placeholder="0"
            value={personalCosts}
            onChange={e => setPersonalCosts(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>Kč / rok (auto, telefony, soukromé výdaje…)</small>
        </div>

        {/* Jednorázové investice */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <TooltipLabel
            htmlFor="one-time-costs"
            label="Jednorázové investice zahrnuté v OPEXu"
            tooltip="Příklad: loni jste zaplatili 150 000 Kč za jednorázovou věc, třeba nový web nebo stěhování provozu. Letos se neopakuje, proto ji můžete přičíst zpět k zisku."
          />
          <input
            id="one-time-costs"
            type="number" min={0} placeholder="0"
            value={oneTimeCosts}
            onChange={e => setOneTimeCosts(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>Kč / rok (redesign, audit, jednorázové projekty…)</small>
        </div>

        {/* Mzdy rodinných příslušníků */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <TooltipLabel
            htmlFor="family-wages"
            label="Mzdy rodinných příslušníků nad tržní hodnotu"
            tooltip="Příklad: Manžel/ka fakturuje firmě 30 000 Kč měsíčně za práci, kterou by externista udělal za 15 000 Kč. Rozdíl 15 000 Kč/měsíc = 180 000 Kč/rok je add-back."
          />
          <input
            id="family-wages"
            type="number" min={0} placeholder="0"
            value={familyWages}
            onChange={e => setFamilyWages(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>Kč / rok (rozdíl mezi vyplacenou a tržní mzdou)</small>
        </div>

        {/* Nadměrný marketingový test */}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <TooltipLabel
            htmlFor="marketing-test"
            label="Nadměrný marketingový test (volitelné)"
            tooltip="Peníze utracené za testování kanálu, který byl následně zastaven. Tyto náklady se v budoucnu neopakují, investor je nezapočítá do provozních nákladů."
          />
          <input
            id="marketing-test"
            type="number" min={0} placeholder="0"
            value={marketingTest}
            onChange={e => setMarketingTest(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>Kč / rok (zastaven kanál, ukončené testy…)</small>
        </div>

        {/* Jednorázové výnosy */}
        <div className="form-group" style={{ marginBottom: '0' }}>
          <TooltipLabel
            htmlFor="one-time-income"
            label="Jednorázové výnosy zahrnuté v zisku"
            tooltip="Příklad: prodej auta nebo stroje, jednorázová dotace, náhrada škody od pojišťovny. Příští rok se nezopakují, proto se od zisku odečítají."
          />
          <input
            id="one-time-income"
            type="number" min={0} placeholder="0"
            value={oneTimeIncome}
            onChange={e => setOneTimeIncome(e.target.value)}
            style={{ textAlign: 'right' }}
          />
          <small style={{ color: 'var(--text-dim)' }}>Kč / rok (od zisku se odečítají)</small>
        </div>
      </div>

      {/* ── Náhled zvolené metriky ────────────────────────────────────────── */}
      {ownerChoice ? (() => {
        const chosen = adjustedEbitda;
        const isOwner = ownerChoice === 'owner';
        return (
          <div style={{
            background: isOwner ? 'rgba(34,197,94,0.06)' : 'rgba(107,63,160,0.1)',
            border: `1px solid ${isOwner ? 'rgba(34,197,94,0.2)' : 'rgba(155,89,212,0.25)'}`,
            borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem', marginBottom: '1.5rem',
          }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: isOwner ? 'rgba(47,143,91,0.45)' : 'var(--purple-light)', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Zisk, ze kterého počítáme hodnotu (Adjusted EBITDA)
            </div>
            <PreviewRow label="Adjusted EBITDA" value={chosen} highlight />
            {isOwner && <PreviewRow label="Pro informaci: zisk pro majitele (SDE)" value={sde} />}
            <DeltaRow label="Nárůst oproti EBITDA z účetnictví" value={chosen - rawEbitda} />
          </div>
        );
      })() : (
        <div style={{ marginBottom: '1.5rem', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
          Vyberte výše, kdo firmu povede, a ukážeme vám odpovídající očištěný zisk.
        </div>
      )}

      {/* ── Dynamická shrnující věta ──────────────────────────────────────── */}
      {hasAnyInput && (() => {
        const bestUplift = adjustedEbitda - rawEbitda;
        if (bestUplift <= 0) return null;
        return (
          <div style={{
            background: 'rgba(107,63,160,0.12)', border: '1px solid rgba(155,89,212,0.3)',
            borderRadius: 'var(--radius-lg)', padding: '0.9rem 1.1rem',
            marginBottom: '1.5rem', fontSize: '0.88rem', color: 'var(--star-white)', lineHeight: 1.6,
          }}>
            Díky těmto úpravám se váš očištěný zisk zvýšil o{' '}
            <strong style={{ color: 'var(--purple-light)' }}>{fmt(bestUplift)} Kč</strong> oproti
            zisku z účetnictví.
          </div>
        );
      })()}

      {/* ── Tlačítka ──────────────────────────────────────────────────────── */}
      <div className="button-group">
        {onBack && (
          <button className="btn-secondary" onClick={onBack}>
            Zpět
          </button>
        )}
        <button className="btn-primary" onClick={handleComplete}>
          Pokračovat →
        </button>
      </div>
    </div>
  );
}
